import { useState } from 'react'
import { AlertTriangle, Info, Plus } from 'lucide-react'
import { useParams } from 'react-router'
import {
  EmptyState, ErrorState, FormDialog, KanbanBoard, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import {
  useCandidateMutations, useCandidates, useRecruitmentBoard, useRejectionReasons,
  useRequisition, useRequisitionMutations,
} from '../api'
import {
  REQUISITION_STATUS_LABELS, STAGE_KIND_LABELS,
} from '../types'
import type { JobApplicationView, RequisitionStatus, StageKind } from '../types'
import { Select } from '@/components/ui/select'

const STATUS_TONE: Record<RequisitionStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  SUBMITTED: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  ON_HOLD: 'warning',
  FILLED: 'info',
  CANCELLED: 'neutral',
}

/** One requisition: its pipeline, its board, and the decisions on it. */
export function RequisitionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const requisition = useRequisition(id)
  useBreadcrumb(requisition.data?.reference ?? 'Requisition')
  const board = useRecruitmentBoard(id ?? null)
  const candidates = useCandidates()
  const reasons = useRejectionReasons()
  const { addStage, submit, decide } = useRequisitionMutations()
  const { apply, moveStage, reject } = useCandidateMutations()

  const [stageDraft, setStageDraft] = useState<{
    stageOrder: string; name: string; stageKind: StageKind; requiresInterview: boolean
  } | null>(null)
  const [applyDraft, setApplyDraft] = useState<{ candidateId: string } | null>(null)
  const [rejectDraft, setRejectDraft] = useState<{
    applicationId: string; rejectionReasonId: string; comment: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (requisition.isPending) return <Skeleton className="h-64" />
  if (requisition.isError) {
    return <ErrorState error={requisition.error} onRetry={() => void requisition.refetch()} />
  }
  if (!requisition.data) return null

  const current = requisition.data
  const status = current.status as RequisitionStatus

  const act = (work: () => Promise<unknown>, fallback: string) => {
    void (async () => {
      setError(null)
      try {
        await work()
      } catch (caught) {
        setError(toApiProblem(caught)?.detail ?? fallback)
      }
    })()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={`${current.reference} — ${current.title}`}
        subtitle={`${current.headcount} head(s) · ${current.departmentName ?? 'no department'}`}
        actions={
          <div className="flex flex-wrap gap-2">
            {status === 'DRAFT' && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setError(null)
                    setStageDraft({
                      stageOrder: String(current.stages.length + 1), name: '',
                      stageKind: 'APPLIED', requiresInterview: false,
                    })
                  }}
                >
                  <Plus className="size-4" aria-hidden="true" />
                  Add stage
                </Button>
                <Button
                  type="button"
                  onClick={() => act(
                    () => submit.mutateAsync(current.id),
                    'That requisition could not be submitted')}
                >
                  Submit for approval
                </Button>
              </>
            )}
            {status === 'SUBMITTED' && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => act(
                    () => decide.mutateAsync({ id: current.id, approved: false }),
                    'That requisition could not be rejected')}
                >
                  Reject
                </Button>
                <Button
                  type="button"
                  onClick={() => act(
                    () => decide.mutateAsync({ id: current.id, approved: true }),
                    'That requisition could not be approved')}
                >
                  Approve
                </Button>
              </>
            )}
            {status === 'APPROVED' && (
              <Button
                type="button"
                onClick={() => { setError(null); setApplyDraft({ candidateId: '' }) }}
              >
                <Plus className="size-4" aria-hidden="true" />
                Add applicant
              </Button>
            )}
          </div>
        }
      />

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <Card className="p-4">
        <dl className="grid gap-4 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-slate-600">Status</dt>
            <dd className="mt-1">
              <StatusBadge
                status={current.status}
                tone={STATUS_TONE[status] ?? 'neutral'}
                label={REQUISITION_STATUS_LABELS[status] ?? current.status}
              />
            </dd>
          </div>
          <div>
            <dt className="text-slate-600">Salary range</dt>
            <dd className="mt-1">
              {current.salaryRangeMin == null && current.salaryRangeMax == null
                ? 'Not set'
                : `${current.salaryRangeMin ?? '?'} – ${current.salaryRangeMax ?? '?'} ${current.currency ?? ''}`}
            </dd>
          </div>
          <div>
            <dt className="text-slate-600">Applicants</dt>
            <dd className="mt-1 font-medium">{current.applicationCount}</dd>
          </div>
          <div>
            <dt className="text-slate-600">Fiscal year</dt>
            <dd className="mt-1">{current.fiscalYear ?? 'Not set'}</dd>
          </div>
        </dl>

        <p className={`mt-4 flex items-start gap-2 rounded-md border p-3 text-sm ${
          !current.budgetChecked
            ? 'border-slate-200 bg-slate-50 text-slate-700'
            : (current.budgetOverage ?? 0) > 0
              ? 'border-amber-200 bg-amber-50 text-amber-900'
              : 'border-emerald-200 bg-emerald-50 text-emerald-900'
        }`}>
          {!current.budgetChecked
            ? <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            : <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />}
          <span>{current.budgetNote ?? 'No budget information.'}</span>
        </p>
      </Card>

      <PageHeader title="Pipeline" subtitle="The stages this hire goes through, in order." />
      <Card>
        {current.stages.length === 0 ? (
          <EmptyState
            title="No stages yet"
            description="A requisition cannot be submitted without them — an application would have nowhere to sit."
          />
        ) : (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-end font-medium">#</th>
                  <th className="p-3 text-start font-medium">Name</th>
                  <th className="p-3 text-start font-medium">Counts as</th>
                  <th className="p-3 text-start font-medium">Needs an interview</th>
                </tr>
              </thead>
              <tbody>
                {current.stages.map((stage) => (
                  <tr key={stage.id} className="border-b border-slate-100">
                    <td className="p-3 text-end text-slate-600">{stage.stageOrder}</td>
                    <td className="p-3">{stage.name}</td>
                    <td className="p-3 text-slate-600">
                      {STAGE_KIND_LABELS[stage.stageKind as StageKind] ?? stage.stageKind}
                    </td>
                    <td className="p-3 text-slate-600">
                      {stage.requiresInterview ? 'Yes' : 'No'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {board.isSuccess && board.data.columns.length > 0 && (
        <>
          <PageHeader
            title="Board"
            subtitle="Every stage appears, including the ones nobody has reached."
          />
          <KanbanBoard
            aria-label="Candidates by pipeline stage"
            columns={board.data.columns.map((column) => ({
              id: column.stageId,
              label: column.stageName,
              items: column.applications,
              emptyHint: 'Nobody has reached this stage.',
            }))}
            itemKey={(application: JobApplicationView) => application.id}
            renderCard={(application: JobApplicationView) => (
              <>
                <p className="text-sm font-medium">{application.candidateName}</p>
                <p className="text-xs text-slate-600">
                  {application.reference} · applied {application.appliedOn}
                </p>
                <p className="text-xs text-slate-600">{application.feedbackProgress}</p>
                {application.rejectionReasonName && (
                  <p className="text-xs text-rose-700">
                    Rejected: {application.rejectionReasonName}
                  </p>
                )}
              </>
            )}
            renderMove={(application: JobApplicationView) => (
              <>
                <label
                  htmlFor={`move-${application.id}`}
                  className="block text-xs text-slate-600"
                >
                  Move {application.candidateName} to
                </label>
                <Select
                  id={`move-${application.id}`}
                  value={application.stageId ?? ''}
                  className="w-full"
                  onChange={(event) => act(
                    () => moveStage.mutateAsync({
                      id: application.id, stageId: event.target.value }),
                    'That candidate could not be moved')}
                >
                  {current.stages.map((stage) => (
                    <option key={stage.id} value={stage.id}>{stage.name}</option>
                  ))}
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  className="mt-1 h-7 w-full text-xs"
                  onClick={() => {
                    setError(null)
                    setRejectDraft({
                      applicationId: application.id, rejectionReasonId: '', comment: '',
                    })
                  }}
                >
                  Reject
                </Button>
              </>
            )}
          />
        </>
      )}

      <FormDialog
        open={stageDraft != null}
        onOpenChange={(open) => { if (!open) setStageDraft(null) }}
        title="Add a pipeline stage"
        description="The name is yours; what it counts as is how the board and the funnel report group it."
        submitLabel="Add stage"
        loading={addStage.isPending}
        error={error}
        onSubmit={() => {
          if (!stageDraft) return
          void (async () => {
            setError(null)
            try {
              await addStage.mutateAsync({
                id: current.id,
                body: {
                  stageOrder: Number(stageDraft.stageOrder),
                  name: stageDraft.name,
                  stageKind: stageDraft.stageKind,
                  requiresInterview: stageDraft.requiresInterview,
                },
              })
              setStageDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That stage could not be added')
            }
          })()
        }}
      >
        {stageDraft && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="stage-order">Position</Label>
                <Input
                  id="stage-order" type="number" value={stageDraft.stageOrder}
                  onChange={(event) =>
                    setStageDraft({ ...stageDraft, stageOrder: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="stage-name">Name</Label>
                <Input
                  id="stage-name" value={stageDraft.name}
                  onChange={(event) => setStageDraft({ ...stageDraft, name: event.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="stage-kind">Counts as</Label>
                <Select
                  id="stage-kind"
                  className="w-full"
                  value={stageDraft.stageKind}
                  onChange={(event) => setStageDraft({
                    ...stageDraft, stageKind: event.target.value as StageKind })}
                >
                  {Object.entries(STAGE_KIND_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Select>
                <p className="mt-1 text-xs text-slate-600">
                  So "Coffee chat" can still be an interview as far as the reports are concerned.
                </p>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={stageDraft.requiresInterview}
                onChange={(event) => setStageDraft({
                  ...stageDraft, requiresInterview: event.target.checked })}
              />
              A candidate cannot reach this stage without a completed interview
            </label>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={applyDraft != null}
        onOpenChange={(open) => { if (!open) setApplyDraft(null) }}
        title="Add an applicant"
        description="They land in the first stage of this requisition's pipeline."
        submitLabel="Add"
        loading={apply.isPending}
        error={error}
        onSubmit={() => {
          if (!applyDraft) return
          void (async () => {
            setError(null)
            try {
              await apply.mutateAsync({
                candidateId: applyDraft.candidateId,
                requisitionId: current.id,
              })
              setApplyDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That application could not be added')
            }
          })()
        }}
      >
        {applyDraft && (
          <div>
            <Label htmlFor="apply-candidate">Candidate</Label>
            <Select
              id="apply-candidate"
              className="w-full"
              value={applyDraft.candidateId}
              onChange={(event) =>
                setApplyDraft({ ...applyDraft, candidateId: event.target.value })}
            >
              <option value="">Choose a candidate…</option>
              {candidates.data?.filter((candidate) => candidate.status === 'ACTIVE')
                .map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.displayName} ({candidate.email ?? candidate.phone})
                  </option>
                ))}
            </Select>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={rejectDraft != null}
        onOpenChange={(open) => { if (!open) setRejectDraft(null) }}
        title="Reject this application"
        description="A reason is required. It is the record that shows the decision was lawful."
        submitLabel="Reject"
        loading={reject.isPending}
        error={error}
        onSubmit={() => {
          if (!rejectDraft) return
          void (async () => {
            setError(null)
            try {
              await reject.mutateAsync({
                id: rejectDraft.applicationId,
                rejectionReasonId: rejectDraft.rejectionReasonId,
                comment: rejectDraft.comment || undefined,
              })
              setRejectDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That application could not be rejected')
            }
          })()
        }}
      >
        {rejectDraft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="reject-reason">Reason</Label>
              <Select
                id="reject-reason"
                className="w-full"
                value={rejectDraft.rejectionReasonId}
                onChange={(event) =>
                  setRejectDraft({ ...rejectDraft, rejectionReasonId: event.target.value })}
              >
                <option value="">Choose a reason…</option>
                {reasons.data?.filter((reason) => reason.active).map((reason) => (
                  <option key={reason.id} value={reason.id}>{reason.name}</option>
                ))}
              </Select>
              {(reasons.data?.length ?? 0) === 0 && (
                <p className="mt-1 text-xs text-amber-700">
                  No rejection reason is defined. None ships with this system — they carry
                  legal weight, so the list has to be yours. Add one in configuration first.
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="reject-comment">Comment</Label>
              <Input
                id="reject-comment" value={rejectDraft.comment}
                onChange={(event) =>
                  setRejectDraft({ ...rejectDraft, comment: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
